import { defineStore } from 'pinia';
import Network from "~/utils/network";

/**
 * One PSD2 sync store, for one aggregator behind one base URL.
 *
 * There were four of these, written out longhand: an A8 and a GoCardless one,
 * each with a business twin. They differed in the base URL, in whether the
 * status and cancel calls name the subject of the sync in the path, and in
 * nothing else - so a fix to one reliably missed the other three.
 *
 * Every store built here answers the same four calls with the same arguments.
 * Which of them a flow is allowed to make is the aggregator's business, not
 * this store's: A8 has no cancel endpoint, and only GoCardless caps syncs per
 * day. Both are stated in utils/psd2/aggregators.js, and nothing calls a store
 * without going through one.
 *
 * @param id      the Pinia store id, kept stable so devtools and any existing
 *                subscriptions still recognise it
 * @param baseUrl everything before /my-sync, /start, /override
 */
export function createPsd2SyncStore({ id, baseUrl }) {
    const urls = {
        // The business endpoints name the subject in the path; the consumer
        // ones take it from the session and are handed nothing.
        mySync: (subject) => subject ? `${baseUrl}/my-sync/${subject}` : `${baseUrl}/my-sync`,
        start: `${baseUrl}/start`,
        cancelSync: (subject) => subject
            ? `${baseUrl}/cancel-my-in-progress-sync/${subject}`
            : `${baseUrl}/cancel-my-in-progress-sync`,
        failSync: (syncId) => `${baseUrl}/failed/${syncId}`,
        mistakeFound: `${baseUrl}/override`,
    };

    return defineStore(id, {
        state: () => ({
            latest: null,
            dailySyncLimitReached: false,
        }),
        actions: {
            start: async function (request) {
                return await Network.http(urls.start, { method: 'POST', body: request });
            },
            getStatus: async function ({ force, flowId, subject } = {}) {
                let urlParams = new URLSearchParams();
                if (force) {
                    urlParams.set('force', force);
                }
                if (flowId) {
                    urlParams.set('flowId', flowId);
                }
                try {
                    let data = await Network.http(`${urls.mySync(subject)}?${urlParams.toString()}`);
                    this.saveLatest(data ?? null);
                    return data;
                } catch (err) {
                    // The daily cap is only ever reported as a status of its own.
                    if (err.statusCode === 429) {
                        this.dailySyncLimitReached = true;
                    }
                    throw err;
                }
            },
            cancelSync: async function (subject) {
                return await Network.http(urls.cancelSync(subject), { method: 'PATCH' });
            },
            /**
             * Closes one running sync by id. The backend refuses to fail a sync
             * that succeeded meanwhile, so this cannot lose a good result.
             */
            failSync: async function (syncId) {
                return await Network.http(urls.failSync(syncId), { method: 'PATCH' });
            },
            mistakeFound: async function (request) {
                return await Network.http(urls.mistakeFound, { method: 'PATCH', body: request });
            },
            saveLatest: function (data) {
                this.latest = data;
            },
        },
        getters: {
            latestSync: (state) => state.latest,
        },
    });
}
